/**
 * TradeShare (PULSE-219, редизайн PULSE-231) — публичная карточка сделки.
 * Без AppShell и auth: /share/trade/:token открывается любым, у кого ссылка.
 *
 * Graphite-лук: карточка на токенах дизайн-системы, крупный PnL-вердикт,
 * графики (цена/OI/CVD/ликвидации) с маркерами входа/выхода, кнопка
 * «Скачать PNG» — карточка для телеграма в два клика.
 */
import { useRef, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { Download } from 'lucide-react'

import { MarketChart, type TradeMarkers } from '@/components/market/MarketChart'
import { useSharedTrade } from '@/hooks/useDashboardApi'
import { downloadNodePng } from '@/lib/sharePng'
import { cn } from '@/lib/cn'

function fmtDate(s: string | null): string {
  if (!s) return '—'
  try {
    return new Date(s).toLocaleString('ru-RU', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    })
  } catch { return '—' }
}

function holdLabel(open: string | null, close: string | null): string {
  if (!open || !close) return '—'
  const m = Math.round((new Date(close).getTime() - new Date(open).getTime()) / 60000)
  if (m < 60) return `${m} мин`
  return `${Math.floor(m / 60)}ч ${m % 60}м`
}

const px = (n: number | null | undefined) => (n == null ? '—' : String(n))

export function Mark() {
  return (
    <span className="inline-flex items-center gap-2">
      <svg width="20" height="20" viewBox="0 0 32 32" aria-hidden="true">
        <rect width="32" height="32" rx="7" fill="var(--elevated)" />
        <path d="M16 6 L26 24 L16 19 L6 24 Z" fill="var(--fire)" />
      </svg>
      <span className="text-sm font-semibold tracking-widest">PULSAR</span>
    </span>
  )
}

export function Stat({ k, v }: { k: string; v: string }) {
  return (
    <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">
      <span className="text-2xs text-textFaint">{k}</span>
      <span className="font-mono text-sm">{v}</span>
    </span>
  )
}

export function ShareFrame({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-ink text-text font-sans flex justify-center p-4">
      <div className="w-full max-w-[880px]">{children}</div>
    </div>
  )
}

export function PngButton({ node, filename }: {
  node: React.RefObject<HTMLDivElement | null>
  filename: string
}) {
  const [busy, setBusy] = useState(false)
  return (
    <button
      onClick={async () => {
        setBusy(true)
        try { await downloadNodePng(node.current, filename) } finally { setBusy(false) }
      }}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border
                 bg-elevated text-textDim border-line hover:text-text hover:border-lineHi transition-colors"
    >
      <Download size={13} />
      {busy ? 'рендер…' : 'скачать PNG'}
    </button>
  )
}

export function TradeShare() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, isError } = useSharedTrade(token)
  const cardRef = useRef<HTMLDivElement | null>(null)

  if (isLoading) {
    return <ShareFrame><div className="py-16 text-center text-textFaint text-sm">Загрузка сделки…</div></ShareFrame>
  }
  if (isError || !data) {
    return <ShareFrame><div className="py-16 text-center text-rose text-sm">Сделка не найдена или ссылка устарела.</div></ShareFrame>
  }

  const t = data.trade
  const isShort = t.direction === 'short'
  const pnl = t.is_open ? t.unrealized_pnl_pct : t.realized_pnl_pct
  const up = (pnl ?? 0) >= 0
  const coin = t.symbol.replace('USDT', '')
  const markers: TradeMarkers = {
    direction: t.direction, entry: data.entry_marker, exit: data.exit_marker,
    tp: t.take_profit_price, sl: t.stop_loss_price,
  }

  return (
    <ShareFrame>
      <div className="flex items-center justify-end mb-2">
        <PngButton node={cardRef} filename={`pulsar-${coin}-${t.direction}`} />
      </div>

      <div ref={cardRef}
        className="bg-surface border border-line rounded-lg shadow-soft overflow-hidden flex flex-col"
        style={{ height: 'calc(100vh - 76px)', minHeight: 560 }}>

        {/* верх: бренд + монета + направление + стратегия */}
        <div className="flex items-center gap-3 flex-wrap px-4 py-3 border-b border-line">
          <Mark />
          <span className="w-px h-4 bg-line" />
          <span className="text-lg font-semibold">
            {coin}<span className="text-textFaint text-xs font-normal">USDT</span>
          </span>
          <span className={cn('pill font-bold',
            isShort ? 'bg-rose-faint text-rose' : 'bg-pulse-faint text-pulse')}>
            {isShort ? 'SHORT' : 'LONG'}
          </span>
          {t.strategy_name && (
            <span className="pill bg-elevated text-textDim">{t.strategy_name}</span>
          )}
          <span className="ml-auto text-2xs text-textFaint font-mono">{fmtDate(t.opened_at)}</span>
        </div>

        {/* вердикт: PnL крупно + факты */}
        <div className="flex items-center gap-5 flex-wrap px-4 py-3 border-b border-line">
          <div className="flex items-baseline gap-3">
            <span className={cn('font-mono text-3xl font-semibold leading-none tracking-tight',
              pnl == null ? 'text-textFaint' : up ? 'text-pulse' : 'text-rose')}>
              {pnl == null ? '—' : `${up ? '+' : ''}${pnl.toFixed(2).replace('-', '−')}%`}
            </span>
            <span className="pill bg-elevated text-textDim">
              {t.is_open ? 'позиция открыта' : (t.exit_reason ?? 'закрыта')}
            </span>
          </div>
          <div className="ml-auto flex items-baseline gap-5 flex-wrap">
            <Stat k="вход" v={px(t.entry_price)} />
            <Stat k="выход" v={t.is_open ? '—' : px(t.exit_price)} />
            <Stat k="TP / SL" v={`${px(t.take_profit_price)} / ${px(t.stop_loss_price)}`} />
            <Stat k="холд" v={t.is_open ? 'в позиции' : holdLabel(t.opened_at, t.closed_at)} />
            <Stat k="плечо" v={t.leverage ? `${t.leverage}×` : '—'} />
          </div>
        </div>

        {/* графики */}
        <div className="flex-1 min-h-0 p-1.5">
          <MarketChart data={data.series} markers={markers} />
        </div>

        <div className="px-4 py-2 border-t border-line text-2xs text-textFaint flex items-center">
          <span>момент входа и выхода размечен на каждом графике</span>
          <span className="ml-auto font-mono">pulsar.paradigmadev.com</span>
        </div>
      </div>
    </ShareFrame>
  )
}
